import React, { useState, useEffect, useRef, useCallback } from 'react';
import { HashRouter, Routes, Route, Navigate, Outlet, useNavigate } from 'react-router-dom';
import BindDnsPage from './pages/BindDnsPage';
import DatabasePage from './pages/DatabasePage';
import LoginPage from './pages/LoginPage';
import SystemdPage from './pages/SystemdPage';
import ServerOpsPage from './pages/ServerOpsPage';
import DashboardPage from './pages/DashboardPage';
import DeployPage from './pages/DeployPage';
import AppLayout from './components/AppLayout';
import type { Theme } from './components/ThemeToggle';
import { CommitModal, serverFlag, STATUS_COLOR } from './deploy/deployWidgets';
import type {
  ServerInfo,
  AppInfo,
  LogEntry,
  Backup,
  CommitInfo,
  ServerStatus,
  DeployStatus,
  DeployState,
  NginxTarget,
} from './deploy/types';
import {
  CheckCircle, XCircle, Loader2, Terminal,
  Wifi, WifiOff, RefreshCw, Globe, Zap,
  ChevronDown, ChevronUp, GitBranch, FolderOpen,
  Play, Package, ArrowRight, Lock, Eye, EyeOff, ShieldCheck,
  RotateCcw, Clock, AlertTriangle, Trash2, Copy,
  Moon, Sun, Monitor, Settings, Server, Plus, Pencil, Github, FileCode2, GripVertical, Activity,
} from 'lucide-react';
import './App.css';
import { API, apiFetch, clearToken, getToken } from './apiClient';

// ─────────────────────────────────────────────────────────────────────────────
// TYPES & HELPERS
// ─────────────────────────────────────────────────────────────────────────────

function loadPasswords(): Record<string, string> {
  try { return JSON.parse(sessionStorage.getItem('srv_passwords') || '{}'); } catch { return {}; }
}
function savePasswords(p: Record<string, string>) {
  try { sessionStorage.setItem('srv_passwords', JSON.stringify(p)); } catch {}
}

// ─────────────────────────────────────────────────────────────────────────────
// NGINX CONFIG MODAL
// ─────────────────────────────────────────────────────────────────────────────

// ── Nginx config templates ──────────────────────────────────────────────────
const NGINX_TEMPLATES: Record<string, (opts: { serverName: string; listenPort: string; proxyPort: string; ssl: boolean }) => string> = {
  'reverse-proxy': ({ serverName, listenPort, proxyPort }) => `server {
    listen ${listenPort};
    server_name ${serverName || '_'};

    location / {
        proxy_pass http://localhost:${proxyPort};
        proxy_http_version 1.1;
        proxy_set_header Upgrade $http_upgrade;
        proxy_set_header Connection 'upgrade';
        proxy_set_header Host $host;
        proxy_set_header X-Real-IP $remote_addr;
        proxy_set_header X-Forwarded-For $proxy_add_x_forwarded_for;
        proxy_cache_bypass $http_upgrade;
    }
}`,
  'static-site': ({ serverName, listenPort }) => `server {
    listen ${listenPort};
    server_name ${serverName || '_'};

    root /var/www/html;
    index index.html index.htm;

    location / {
        try_files $uri $uri/ /index.html;
    }

    location ~* \\.(js|css|png|jpg|jpeg|gif|ico|svg|woff2?)$ {
        expires 1y;
        add_header Cache-Control "public, immutable";
    }
}`,
  'redirect': ({ serverName }) => `server {
    listen 80;
    server_name ${serverName || '_'};
    return 301 https://$host$request_uri;
}`,
  'ssl': ({ serverName, proxyPort }) => `server {
    listen 80;
    server_name ${serverName || '_'};
    return 301 https://$host$request_uri;
}

server {
    listen 443 ssl http2;
    server_name ${serverName || '_'};

    ssl_certificate     /etc/letsencrypt/live/${serverName || 'example.com'}/fullchain.pem;
    ssl_certificate_key /etc/letsencrypt/live/${serverName || 'example.com'}/privkey.pem;
    ssl_protocols       TLSv1.2 TLSv1.3;
    ssl_ciphers         HIGH:!aNULL:!MD5;

    location / {
        proxy_pass http://localhost:${proxyPort};
        proxy_http_version 1.1;
        proxy_set_header Upgrade $http_upgrade;
        proxy_set_header Connection 'upgrade';
        proxy_set_header Host $host;
        proxy_set_header X-Real-IP $remote_addr;
        proxy_set_header X-Forwarded-For $proxy_add_x_forwarded_for;
        proxy_cache_bypass $http_upgrade;
    }
}`,
  'blank': () => ``,
};

const TEMPLATE_LABELS: Record<string, string> = {
  'reverse-proxy': 'Reverse Proxy (Node / Python)',
  'static-site':  'Static Site',
  'redirect':     'HTTP → HTTPS Redirect',
  'ssl':          'SSL / HTTPS Full',
  'blank':        'Blank',
};

// ── Nginx visual builder types ───────────────────────────────────────────────
type NginxLocType = 'static' | 'proxy' | 'websocket' | 'alias' | 'custom';
interface NginxLocBlock {
  id: string; path: string; type: NginxLocType;
  root?: string; tryFiles?: string;
  upstream?: string; stdProxyHeaders?: boolean;
  wsReadTimeout?: string; wsSendTimeout?: string; wsBufferingOff?: boolean;
  aliasPath?: string; expires?: string; cacheControl?: string; disableAccessLog?: boolean; includeMimeTypes?: boolean;
  rawBody?: string;
}
interface NginxVisualCfg {
  serverName: string; ipv6: boolean; ssl: boolean;
  sslCert: string; sslKey: string; sslOptions: string; sslDhparam: string;
  httpRedirect: boolean; mapBlock: boolean;
  accessLog: string; errorLog: string;
  locations: NginxLocBlock[];
  secHdrXFrame: string; secHdrXXSS: string; secHdrXContent: string; secHdrHSTS: string;
}
const BLANK_VCFG = (): NginxVisualCfg => ({
  serverName: '', ipv6: false, ssl: false,
  sslCert: '', sslKey: '', sslOptions: '', sslDhparam: '',
  httpRedirect: false, mapBlock: false,
  accessLog: '', errorLog: '',
  locations: [],
  secHdrXFrame: '', secHdrXXSS: '', secHdrXContent: '', secHdrHSTS: '',
});
const BLANK_LOC = (): NginxLocBlock => ({
  id: Math.random().toString(36).slice(2) + Date.now().toString(36),
  path: '/', type: 'proxy', upstream: '', stdProxyHeaders: true,
});

function nginxExt(text: string, re: RegExp): string {
  const m = text.match(re);
  return m ? (m[1] || '').replace(/\s*#[^\n]*/, '').trim() : '';
}

function extractNginxLocBlocks(serverBody: string): NginxLocBlock[] {
  const locs: NginxLocBlock[] = [];
  const locRe = /^[ \t]*location\s+([^\n{]+?)\s*\{/gm;
  let m: RegExpExecArray | null;
  while ((m = locRe.exec(serverBody)) !== null) {
    const path = m[1].trim();
    const bi = m.index + m[0].length - 1;
    let depth = 1, j = bi + 1;
    while (j < serverBody.length && depth > 0) {
      if (serverBody[j] === '{') depth++;
      else if (serverBody[j] === '}') depth--;
      j++;
    }
    const body = serverBody.slice(bi + 1, j - 1);
    locRe.lastIndex = j;
    const id  = Math.random().toString(36).slice(2);
    const px  = nginxExt(body, /proxy_pass\s+([^;]+);/);
    const rt  = nginxExt(body, /\broot\s+([^;]+);/);
    const al  = nginxExt(body, /\balias\s+([^;]+);/);
    const tf  = nginxExt(body, /try_files\s+([^;]+);/);
    const isWS = /proxy_set_header\s+Upgrade/.test(body);
    if (al) {
      locs.push({ id, path, type: 'alias', aliasPath: al,
        expires: nginxExt(body, /\bexpires\s+([^;]+);/) || undefined,
        cacheControl: nginxExt(body, /Cache-Control\s+"?([^";#\n]+)"?/) || undefined,
        disableAccessLog: /access_log\s+off/.test(body),
        includeMimeTypes: /include[^;]*mime\.types/.test(body) });
    } else if (px && isWS) {
      locs.push({ id, path, type: 'websocket', upstream: px,
        wsReadTimeout: nginxExt(body, /proxy_read_timeout\s+([^;]+);/) || '600s',
        wsSendTimeout: nginxExt(body, /proxy_send_timeout\s+([^;]+);/) || '600s',
        wsBufferingOff: /proxy_buffering\s+off/.test(body) });
    } else if (px) {
      locs.push({ id, path, type: 'proxy', upstream: px, stdProxyHeaders: /proxy_set_header\s+Host/.test(body) });
    } else if (rt || tf) {
      locs.push({ id, path, type: 'static', root: rt || undefined, tryFiles: tf || undefined });
    } else {
      locs.push({ id, path, type: 'custom', rawBody: body.trim() });
    }
  }
  return locs;
}

function parseNginxVisualCfg(raw: string): NginxVisualCfg {
  const cfg = BLANK_VCFG();
  cfg.secHdrXFrame = ''; cfg.secHdrXXSS = ''; cfg.secHdrXContent = ''; cfg.secHdrHSTS = '';
  cfg.locations = [];
  cfg.mapBlock = /map\s+\$http_upgrade/.test(raw);
  const blocks: string[] = [];
  let i = 0;
  while (i < raw.length) {
    const re = /\bserver\s*\{/g; re.lastIndex = i;
    const mt = re.exec(raw); if (!mt) break;
    const bi = mt.index + mt[0].length - 1;
    let depth = 1, j = bi + 1;
    while (j < raw.length && depth > 0) { if (raw[j] === '{') depth++; else if (raw[j] === '}') depth--; j++; }
    blocks.push(raw.slice(bi + 1, j - 1)); i = j;
  }
  for (const body of blocks) {
    const hasSSL = /listen.*443.*ssl|listen.*ssl.*443/.test(body);
    if (/listen.*:?80\b/.test(body) && /return\s+301\s+https/.test(body) && !hasSSL) { cfg.httpRedirect = true; continue; }
    cfg.serverName = nginxExt(body, /server_name\s+([^;]+);/) || cfg.serverName;
    cfg.ipv6       = /listen\s+\[::\]/.test(body);
    const cert = nginxExt(body, /ssl_certificate\s+([^;#\n]+)/);
    if (cert) {
      cfg.ssl = true; cfg.sslCert = cert;
      cfg.sslKey     = nginxExt(body, /ssl_certificate_key\s+([^;#\n]+)/);
      cfg.sslOptions = nginxExt(body, /include\s+([^;]*options-ssl[^;#\n]+)/);
      cfg.sslDhparam = nginxExt(body, /ssl_dhparam\s+([^;#\n]+)/);
    }
    cfg.accessLog = nginxExt(body, /access_log\s+([^;#\n]+)/) || cfg.accessLog;
    cfg.errorLog  = nginxExt(body, /error_log\s+([^;#\n]+)/)  || cfg.errorLog;
    for (const hm of body.matchAll(/^    add_header\s+(\S+)\s+"?([^";#\n]+)"?/gm)) {
      const [, name, val] = hm;
      if      (name === 'X-Frame-Options')           cfg.secHdrXFrame   = val.trim();
      else if (name === 'X-XSS-Protection')          cfg.secHdrXXSS     = val.trim();
      else if (name === 'X-Content-Type-Options')    cfg.secHdrXContent = val.trim();
      else if (name === 'Strict-Transport-Security') cfg.secHdrHSTS     = val.trim();
    }
    cfg.locations = extractNginxLocBlocks(body);
  }
  return cfg;
}

function generateNginxConfig(cfg: NginxVisualCfg): string {
  const sn = cfg.serverName.trim() || '_';
  const ls: string[] = [];
  const L = (...s: string[]) => ls.push(...s);
  if (cfg.mapBlock) {
    L('# Put this near the top (outside server blocks) so Connection header is correct',
      'map $http_upgrade $connection_upgrade {', '    default upgrade;', "    ''      close;", '}', '');
  }
  L(cfg.ssl ? '# HTTPS Server Configuration' : '# HTTP Server Configuration', 'server {',
    `    server_name ${sn};`, '');
  if (cfg.ssl) {
    if (cfg.ipv6) L('    listen [::]:443 ssl http2; # IPv6');
    L('    listen 443 ssl http2;      # IPv4');
    const primaryHost = sn.split(/\s+/)[0] || '_';
    const certPath = cfg.sslCert.trim() || `/etc/letsencrypt/live/${primaryHost}/fullchain.pem`;
    const keyPath  = cfg.sslKey.trim()  || `/etc/letsencrypt/live/${primaryHost}/privkey.pem`;
    const sslLines: string[] = [];
    sslLines.push(`    ssl_certificate     ${certPath};`);
    sslLines.push(`    ssl_certificate_key ${keyPath};`);
    const sslOptsInc = cfg.sslOptions.trim() || '/etc/letsencrypt/options-ssl-nginx.conf';
    const sslOptsDh  = cfg.sslDhparam.trim()  || '/etc/letsencrypt/ssl-dhparams.pem';
    sslLines.push(`    include ${sslOptsInc};`);
    sslLines.push(`    ssl_dhparam ${sslOptsDh};`);
    if (sslLines.length) { L(''); L(...sslLines); }
  } else {
    if (cfg.ipv6) L('    listen [::]:80;');
    L('    listen 80;');
  }
  if (cfg.accessLog || cfg.errorLog) {
    L('');
    if (cfg.accessLog) L(`    access_log ${cfg.accessLog};`);
    if (cfg.errorLog)  L(`    error_log  ${cfg.errorLog};`);
  }
  for (const loc of cfg.locations) {
    L('', `    location ${loc.path} {`);
    switch (loc.type) {
      case 'static':
        if (loc.root)     L(`        root ${loc.root};`);
        if (loc.tryFiles) L(`        try_files ${loc.tryFiles};`);
        break;
      case 'alias':
        if (loc.includeMimeTypes) L('        include /etc/nginx/mime.types;');
        if (loc.aliasPath)        L(`        alias ${loc.aliasPath};`);
        if (loc.expires)          L(`        expires ${loc.expires};`);
        if (loc.disableAccessLog) L('        access_log off;');
        if (loc.cacheControl)     L(`        add_header Cache-Control "${loc.cacheControl}";`);
        break;
      case 'proxy':
        L(`        proxy_pass ${loc.upstream || ''};`, '        proxy_http_version 1.1;');
        if (loc.stdProxyHeaders !== false)
          L('        proxy_set_header Host $host;', '        proxy_set_header X-Real-IP $remote_addr;',
            '        proxy_set_header X-Forwarded-For $proxy_add_x_forwarded_for;', '        proxy_set_header X-Forwarded-Proto $scheme;');
        break;
      case 'websocket':
        L(`        proxy_pass ${loc.upstream || ''};`, '        proxy_http_version 1.1;',
          '        proxy_set_header Upgrade $http_upgrade;', '        proxy_set_header Connection $connection_upgrade;',
          '        proxy_set_header Host $host;',
          `        proxy_read_timeout ${loc.wsReadTimeout || '600s'};`, `        proxy_send_timeout ${loc.wsSendTimeout || '600s'};`);
        if (loc.wsBufferingOff !== false) L('        proxy_buffering off;');
        break;
      case 'custom':
        (loc.rawBody || '').split('\n').forEach(l => L(`        ${l}`));
        break;
    }
    L('    }');
  }
  const sec: string[] = [];
  if (cfg.secHdrXFrame)   sec.push(`    add_header X-Frame-Options "${cfg.secHdrXFrame}";`);
  if (cfg.secHdrXXSS)     sec.push(`    add_header X-XSS-Protection "${cfg.secHdrXXSS}";`);
  if (cfg.secHdrXContent) sec.push(`    add_header X-Content-Type-Options "${cfg.secHdrXContent}";`);
  if (cfg.secHdrHSTS)     sec.push(`    add_header Strict-Transport-Security "${cfg.secHdrHSTS}" always;`);
  if (sec.length) { L(''); L(...sec); }
  L('}');
  if (cfg.httpRedirect && cfg.ssl) {
    L('', '# HTTP Server Configuration (Redirect to HTTPS)', 'server {', `    server_name ${sn};`, '    listen 80;');
    if (cfg.ipv6) L('    listen [::]:80;');
    L('    return 301 https://$host$request_uri;', '}');
  }
  return ls.join('\n');
}

const LOC_TYPE_LABELS: Record<NginxLocType, string> = {
  proxy: 'Proxy', websocket: 'WebSocket', static: 'Static', alias: 'Alias', custom: 'Custom',
};

/** Inline location editor — rendered directly under the row being edited */
function VbLocEditorForm({
  locForm, pL, onSave, onCancel, isNew,
}: {
  locForm: NginxLocBlock;
  pL: (patch: Partial<NginxLocBlock>) => void;
  onSave: () => void;
  onCancel: () => void;
  isNew: boolean;
}) {
  return (
    <div className="vb-loc-form vb-loc-form-inline">
      <div className="vb-grid-2">
        <label className="vb-field">
          <span>Path</span>
          <input className="cfg-input" placeholder="/ or /admin" value={locForm.path} onChange={e => pL({ path: e.target.value })} />
        </label>
        <label className="vb-field">
          <span>Type</span>
          <select className="commit-select" value={locForm.type} onChange={e => pL({ type: e.target.value as NginxLocType })}>
            <option value="proxy">Proxy</option>
            <option value="websocket">WebSocket</option>
            <option value="static">Static (root)</option>
            <option value="alias">Alias</option>
            <option value="custom">Custom</option>
          </select>
        </label>
      </div>
      {(locForm.type === 'proxy' || locForm.type === 'websocket') && (
        <div className="vb-grid-2">
          <label className="vb-field vb-full">
            <span>Upstream (e.g. http://127.0.0.1:8004)</span>
            <input className="cfg-input" value={locForm.upstream || ''} onChange={e => pL({ upstream: e.target.value })} />
          </label>
          {locForm.type === 'proxy' && (
            <label className="vb-field vb-full">
              <label className="vb-check"><input type="checkbox" checked={locForm.stdProxyHeaders !== false} onChange={e => pL({ stdProxyHeaders: e.target.checked })} /> Standard proxy headers</label>
            </label>
          )}
          {locForm.type === 'websocket' && (
            <>
              <label className="vb-field"><span>Read timeout</span><input className="cfg-input" placeholder="600s" value={locForm.wsReadTimeout || ''} onChange={e => pL({ wsReadTimeout: e.target.value })} /></label>
              <label className="vb-field"><span>Send timeout</span><input className="cfg-input" placeholder="600s" value={locForm.wsSendTimeout || ''} onChange={e => pL({ wsSendTimeout: e.target.value })} /></label>
              <label className="vb-field vb-full"><label className="vb-check"><input type="checkbox" checked={locForm.wsBufferingOff !== false} onChange={e => pL({ wsBufferingOff: e.target.checked })} /> Disable proxy buffering</label></label>
            </>
          )}
        </div>
      )}
      {locForm.type === 'static' && (
        <div className="vb-grid-2">
          <label className="vb-field"><span>Root</span><input className="cfg-input" placeholder="/var/www/app" value={locForm.root || ''} onChange={e => pL({ root: e.target.value })} /></label>
          <label className="vb-field"><span>try_files</span><input className="cfg-input" placeholder="$uri $uri/ /index.html" value={locForm.tryFiles || ''} onChange={e => pL({ tryFiles: e.target.value })} /></label>
        </div>
      )}
      {locForm.type === 'alias' && (
        <div className="vb-grid-2">
          <label className="vb-field vb-full"><span>Alias path</span><input className="cfg-input" value={locForm.aliasPath || ''} onChange={e => pL({ aliasPath: e.target.value })} /></label>
          <label className="vb-field"><span>Expires</span><input className="cfg-input" value={locForm.expires || ''} onChange={e => pL({ expires: e.target.value })} /></label>
          <label className="vb-field"><span>Cache-Control</span><input className="cfg-input" value={locForm.cacheControl || ''} onChange={e => pL({ cacheControl: e.target.value })} /></label>
          <div className="vb-field vb-full vb-checks">
            <label className="vb-check"><input type="checkbox" checked={!!locForm.disableAccessLog} onChange={e => pL({ disableAccessLog: e.target.checked })} /> access_log off</label>
            <label className="vb-check"><input type="checkbox" checked={!!locForm.includeMimeTypes} onChange={e => pL({ includeMimeTypes: e.target.checked })} /> include mime.types</label>
          </div>
        </div>
      )}
      {locForm.type === 'custom' && (
        <label className="vb-field vb-full">
          <span>Directives (inside location block)</span>
          <textarea className="nginx-textarea vb-raw-ta" value={locForm.rawBody || ''} onChange={e => pL({ rawBody: e.target.value })} spellCheck={false} />
        </label>
      )}
      <div className="vb-loc-form-btns">
        <button className="btn btn-deploy btn-sm" type="button" onClick={onSave}><CheckCircle size={12} /> {isNew ? 'Add' : 'Update'}</button>
        <button className="btn btn-ghost btn-sm" type="button" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function NginxModal({ target, passwords, onClose }: { target: NginxTarget; passwords: Record<string, string>; onClose: () => void; }) {
  // ── tabs (Raw Editor first — Visual Builder does not auto-import)
  const [nginxTab,     setNginxTab]     = useState<'visual' | 'config' | 'maintenance'>('config');

  // ── visual builder state (empty until user imports or fills manually)
  const [vcfg,         setVcfg]         = useState<NginxVisualCfg>(() => BLANK_VCFG());
  const [vbMoreOpen,   setVbMoreOpen]   = useState(false);
  const [locEditOpen,  setLocEditOpen]  = useState(false);
  const [locEditId,    setLocEditId]    = useState<string | null>(null);
  const [locForm,      setLocForm]      = useState<NginxLocBlock>(BLANK_LOC());
  const [draggingLocId, setDraggingLocId] = useState<string | null>(null);
  const pV = (patch: Partial<NginxVisualCfg>) => setVcfg(p => ({ ...p, ...patch }));
  const pL = (patch: Partial<NginxLocBlock>)  => setLocForm(p => ({ ...p, ...patch }));

  // ── config editor state
  const [siteFiles,    setSiteFiles]    = useState<string[]>([]);
  const [selectedFile, setSelectedFile] = useState(target.nginxSite || '');
  const [content,      setContent]      = useState('');
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [testing,      setTesting]      = useState(false);
  const [error,        setError]        = useState('');
  const [testResult,   setTestResult]   = useState<{ ok: boolean; output: string } | null>(null);

  // ── new file panel
  const [newFileOpen,  setNewFileOpen]  = useState(false);
  const [newFileName,  setNewFileName]  = useState('');
  const [newTemplate,  setNewTemplate]  = useState('reverse-proxy');
  const [creating,     setCreating]     = useState(false);

  // ── rename panel
  const [renameOpen,   setRenameOpen]   = useState(false);
  const [renameTo,     setRenameTo]     = useState('');
  const [renaming,     setRenaming]     = useState(false);
  const [renameErr,    setRenameErr]    = useState('');

  // ── quick config panel
  const [quickOpen,    setQuickOpen]    = useState(false);
  const [quick,        setQuick]        = useState({ serverName: '', listenPort: '80', proxyPort: '3000', ssl: false });

  // ── maintenance state
  const [maintFileName,  setMaintFileName]  = useState(target.nginxSite || '');
  const [maintStatus,    setMaintStatus]    = useState<'active' | 'inactive' | 'unknown'>('unknown');
  const [maintLoading,   setMaintLoading]   = useState(false);
  const [toggling,       setToggling]       = useState(false);
  const [maintHtmlPath,  setMaintHtmlPath]  = useState(target.maintenancePath || '');
  const [maintHtml,      setMaintHtml]      = useState('');
  const [maintHtmlLoading, setMaintHtmlLoading] = useState(false);
  const [savingHtml,     setSavingHtml]     = useState(false);
  const [maintMsg,       setMaintMsg]       = useState<{ ok: boolean; text: string } | null>(null);
  const [pathSaving,     setPathSaving]     = useState(false);
  const [pathSaved,      setPathSaved]      = useState(false);

  const [sslCertStatus, setSslCertStatus] = useState<{
    exists: boolean;
    expired: boolean;
    certPath: string;
    primaryHost: string;
    daysRemaining: number | null;
    notAfterDisplay: string | null;
  } | null>(null);
  const [sslCertLoading, setSslCertLoading] = useState(false);
  const [sslCertbotRunning, setSslCertbotRunning] = useState(false);
  const [sslCertMsg, setSslCertMsg] = useState<string | null>(null);
  const [certbotEmail, setCertbotEmail] = useState(() => {
    try { return localStorage.getItem('vb-certbot-email') || ''; } catch { return ''; }
  });

  const overlayRef = useRef<HTMLDivElement>(null);
  const locEditorRef = useRef<HTMLDivElement>(null);
  const locDragGhostRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    setSslCertStatus(null);
    setSslCertMsg(null);
  }, [vcfg.serverName, vcfg.sslCert]);

  useEffect(() => {
    if (!locEditOpen || nginxTab !== 'visual') return;
    const id = requestAnimationFrame(() => {
      locEditorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
    return () => cancelAnimationFrame(id);
  }, [locEditOpen, locEditId, nginxTab]);

  const post = async (path: string, body: object) => {
    const r = await apiFetch(`${API}${path}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ passwords, ...body }),
    });
    return r.json();
  };

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  // Initial load of sites-enabled list + selected file content
  useEffect(() => {
    (async () => {
      setLoading(true); setError('');
      try {
        const r = await post(`/api/nginx/${target.appId}/${target.serverId}/read`, {});
        if (r.error) { setError(r.error); return; }
        setSiteFiles(r.siteFiles || []);
        setSelectedFile(r.siteName || '');
        setContent(r.content || '');
      } catch (e: any) { setError(e.message); }
      finally { setLoading(false); }
    })();
  }, []); // eslint-disable-line

  const loadFile = async (fileName: string, opts?: { syncVisual?: boolean }) => {
    if (!fileName) return;
    setLoading(true); setError(''); setTestResult(null);
    try {
      const r = await post(`/api/nginx/${target.appId}/${target.serverId}/file`, { fileName });
      if (r.error) { setError(r.error); return; }
      const text = r.content || '';
      setContent(text);
      setSelectedFile(fileName);
      if (opts?.syncVisual) {
        setVcfg(text.trim() ? parseNginxVisualCfg(text) : BLANK_VCFG());
        setLocEditOpen(false);
        setLocEditId(null);
      }
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  };

  const testConfig = async () => {
    setTesting(true); setTestResult(null); setError('');
    try {
      const r = await post(`/api/nginx/${target.appId}/${target.serverId}/test`, {});
      if (r.error) { setError(r.error); return; }
      setTestResult({ ok: r.success, output: r.output || '' });
    } catch (e: any) { setError(e.message); }
    finally { setTesting(false); }
  };

  const saveConfig = async () => {
    if (!selectedFile) return;
    setSaving(true); setError(''); setTestResult(null);
    try {
      const r = await post(`/api/nginx/${target.appId}/${target.serverId}/save`, { fileName: selectedFile, content });
      if (r.error) { setError(r.error); return; }
      setTestResult({ ok: r.success, output: r.testOutput || '' });
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  const createFile = async () => {
    if (!newFileName.trim()) return;
    setCreating(true); setError('');
    try {
      const generatedContent = NGINX_TEMPLATES[newTemplate]({ serverName: quick.serverName || newFileName, listenPort: quick.listenPort, proxyPort: quick.proxyPort, ssl: quick.ssl });
      const r = await post(`/api/nginx/${target.appId}/${target.serverId}/create`, { fileName: newFileName.trim(), content: generatedContent });
      if (r.error) { setError(r.error); return; }
      const updated = [...siteFiles, newFileName.trim()].filter((v, i, a) => a.indexOf(v) === i);
      setSiteFiles(updated);
      setContent(generatedContent);
      setSelectedFile(newFileName.trim());
      setNewFileOpen(false);
      setNewFileName('');
      setVcfg(parseNginxVisualCfg(generatedContent));
      setLocEditOpen(false);
      setLocEditId(null);
      if (!r.success) setTestResult({ ok: false, output: r.testOutput || '' });
    } catch (e: any) { setError(e.message); }
    finally { setCreating(false); }
  };

  const renameFile = async () => {
    if (!renameTo.trim() || !selectedFile) return;
    setRenaming(true); setRenameErr('');
    try {
      const r = await post(`/api/nginx/${target.appId}/${target.serverId}/rename`, { oldName: selectedFile, newName: renameTo.trim() });
      if (r.error) { setRenameErr(r.error); return; }
      if (!r.success) { setRenameErr(r.testOutput || 'nginx -t failed — rename rolled back'); return; }
      const updated = siteFiles.map(f => f === selectedFile ? renameTo.trim() : f);
      setSiteFiles(updated);
      setSelectedFile(renameTo.trim());
      setRenameOpen(false);
      setRenameTo('');
    } catch (e: any) { setRenameErr(e.message); }
    finally { setRenaming(false); }
  };

  const applyQuickConfig = () => {
    const generated = NGINX_TEMPLATES['reverse-proxy'](quick);
    setContent(generated);
    setTestResult(null);
    setQuickOpen(false);
  };

  // Maintenance tab helpers
  const loadMaintStatus = async (fileName: string) => {
    if (!fileName) return;
    setMaintLoading(true); setMaintStatus('unknown');
    try {
      const r = await post(`/api/nginx/${target.appId}/${target.serverId}/maintenance/status`, { fileName });
      if (!r.error) setMaintStatus(r.active ? 'active' : 'inactive');
    } catch (_) {}
    finally { setMaintLoading(false); }
  };

  const loadMaintHtml = async () => {
    if (!maintHtmlPath.trim()) return;
    setMaintHtmlLoading(true);
    try {
      const r = await post(`/api/nginx/${target.appId}/${target.serverId}/maintenance/html`, { maintenancePath: maintHtmlPath });
      if (!r.error) setMaintHtml(r.content || '');
    } catch (_) {}
    finally { setMaintHtmlLoading(false); }
  };

  // Sync maintFileName when siteFiles load (use app's nginxSite, else first file)
  useEffect(() => {
    if (siteFiles.length > 0 && !maintFileName) {
      setMaintFileName(siteFiles[0]);
    }
  }, [siteFiles]); // eslint-disable-line

  useEffect(() => {
    if (nginxTab === 'maintenance') {
      const fn = maintFileName || selectedFile;
      if (fn) loadMaintStatus(fn);
      if (maintHtmlPath && maintHtml === '') loadMaintHtml();
    }
  }, [nginxTab]); // eslint-disable-line

  const toggleMaintenance = async () => {
    if (!maintFileName) return;
    setToggling(true); setMaintMsg(null);
    try {
      const r = await post(`/api/nginx/${target.appId}/${target.serverId}/maintenance/toggle`, {
        fileName: maintFileName, enable: maintStatus !== 'active', maintenancePath: maintHtmlPath,
      });
      if (r.error) { setMaintMsg({ ok: false, text: r.error }); return; }
      setMaintStatus(r.active ? 'active' : 'inactive');
      setMaintMsg({ ok: r.success, text: r.testOutput || (r.success ? 'Done.' : 'nginx -t failed') });
    } catch (e: any) { setMaintMsg({ ok: false, text: e.message }); }
    finally { setToggling(false); }
  };

  const saveMaintHtml = async () => {
    if (!maintHtmlPath.trim()) return;
    setSavingHtml(true); setMaintMsg(null);
    try {
      const r = await post(`/api/nginx/${target.appId}/${target.serverId}/maintenance/html/save`, { maintenancePath: maintHtmlPath, content: maintHtml });
      if (r.error) { setMaintMsg({ ok: false, text: r.error }); return; }
      setMaintMsg({ ok: true, text: 'Maintenance page saved.' });
    } catch (e: any) { setMaintMsg({ ok: false, text: e.message }); }
    finally { setSavingHtml(false); }
  };

  const saveLocForm = () => {
    const newLoc = { ...locForm, id: locForm.id || (Math.random().toString(36).slice(2)) };
    if (locEditId) {
      pV({ locations: vcfg.locations.map(l => l.id === locEditId ? newLoc : l) });
    } else {
      pV({ locations: [...vcfg.locations, newLoc] });
    }
    setLocEditOpen(false); setLocEditId(null);
  };

  const handleLocDragStart = (e: React.DragEvent, locId: string) => {
    e.dataTransfer.setData('application/x-vb-loc', locId);
    e.dataTransfer.setData('text/plain', locId);
    e.dataTransfer.effectAllowed = 'move';
    setDraggingLocId(locId);

    const grip = e.currentTarget as HTMLElement;
    const row = grip.closest('.vb-loc-row') as HTMLElement | null;
    if (!row) return;

    locDragGhostRef.current?.remove();
    locDragGhostRef.current = null;

    const rect = row.getBoundingClientRect();
    const clone = row.cloneNode(true) as HTMLElement;
    clone.classList.add('vb-loc-drag-ghost');
    clone.style.width = `${rect.width}px`;
    clone.style.boxSizing = 'border-box';
    clone.style.position = 'fixed';
    clone.style.left = '-10000px';
    clone.style.top = '0';
    clone.style.pointerEvents = 'none';
    clone.style.margin = '0';
    clone.style.zIndex = '2147483647';
    document.body.appendChild(clone);
    locDragGhostRef.current = clone;

    const ox = e.clientX - rect.left;
    const oy = e.clientY - rect.top;
    try {
      e.dataTransfer.setDragImage(clone, ox, oy);
    } catch {
      /* Safari edge cases */
    }
  };

  const handleLocDragEnd = () => {
    setDraggingLocId(null);
    locDragGhostRef.current?.remove();
    locDragGhostRef.current = null;
  };
  const handleLocDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const handleLocDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    const fromId = e.dataTransfer.getData('application/x-vb-loc');
    if (!fromId || fromId === targetId) return;
    const ls = [...vcfg.locations];
    const fromIdx = ls.findIndex(l => l.id === fromId);
    const toIdx   = ls.findIndex(l => l.id === targetId);
    if (fromIdx < 0 || toIdx < 0) return;
    const [item] = ls.splice(fromIdx, 1);
    ls.splice(toIdx, 0, item);
    pV({ locations: ls });
  };

  const savePathToDB = async (path: string) => {
    if (!path.trim() || path.trim() === target.maintenancePath) return;
    setPathSaving(true); setPathSaved(false);
    try {
      await apiFetch(`${API}/api/apps/${target.appId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ maintenancePath: path.trim() }),
      });
      target.maintenancePath = path.trim(); // update reference so re-blur doesn't re-save
      setPathSaved(true);
      setTimeout(() => setPathSaved(false), 2500);
    } catch (_) {}
    finally { setPathSaving(false); }
  };

  const refreshSslStatus = async () => {
    const ph = vcfg.serverName.trim().split(/\s+/).filter(Boolean)[0] || '';
    if (!ph) { setSslCertMsg('Enter a server name (domain) first.'); return; }
    setSslCertLoading(true);
    setSslCertMsg(null);
    try {
      const r = await post(`/api/nginx/${target.appId}/${target.serverId}/ssl/status`, {
        primaryHost: ph,
        certPath: vcfg.sslCert.trim() || undefined,
      });
      if (r.error) {
        setSslCertStatus(null);
        setSslCertMsg(r.error);
        return;
      }
      setSslCertStatus(r);
    } catch (e: any) {
      setSslCertStatus(null);
      setSslCertMsg(e?.message || String(e));
    } finally {
      setSslCertLoading(false);
    }
  };

  const runCertbotObtain = async () => {
    const domains = vcfg.serverName.trim().split(/\s+/).map(s => s.trim()).filter(Boolean);
    if (!domains.length) { setSslCertMsg('Enter a server name (domain) first.'); return; }
    const email = certbotEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setSslCertMsg('Enter a valid email for Let\'s Encrypt.');
      return;
    }
    const forceRenewal = !!(sslCertStatus?.exists && sslCertStatus?.expired);
    setSslCertbotRunning(true);
    setSslCertMsg(null);
    try {
      const r = await post(`/api/nginx/${target.appId}/${target.serverId}/ssl/certbot`, {
        domains,
        email,
        forceRenewal,
      });
      if (r.error) {
        setSslCertMsg(r.error);
        return;
      }
      if (!r.success) {
        setSslCertMsg(r.output || 'Certbot failed.');
        return;
      }
      setSslCertMsg(r.output ? `Certificate installed.\n\n${r.output}` : 'Certificate obtained. nginx reloaded.');
      await refreshSslStatus();
    } catch (e: any) {
      setSslCertMsg(e?.message || String(e));
    } finally {
      setSslCertbotRunning(false);
    }
  };

  const sslPrimaryHost = vcfg.serverName.trim().split(/\s+/).filter(Boolean)[0] ?? '';
  const sslNeedsCertObtain = !sslCertStatus || !sslCertStatus.exists || sslCertStatus.expired;

  return (
    <div className="nginx-overlay" ref={overlayRef} onMouseDown={e => { if (e.target === overlayRef.current) onClose(); }}>
      <div className="nginx-dialog">

        {/* Header */}
        <div className="nginx-header">
          <span className="nginx-title">
            <FileCode2 size={15} />
            Nginx — <strong>{target.appName}</strong> on {target.serverLabel}
          </span>
          <button className="cm-close" onClick={onClose}><XCircle size={16} /></button>
        </div>

        {/* Tab switcher */}
        <div className="nginx-tabs">
          <button className={`nginx-tab ${nginxTab === 'visual' ? 'active' : ''}`} onClick={() => setNginxTab('visual')}>
            <Settings size={12} /> Visual Builder
          </button>
          <button className={`nginx-tab ${nginxTab === 'config' ? 'active' : ''}`} onClick={() => setNginxTab('config')}>
            <FileCode2 size={12} /> Raw Editor
          </button>
          <button className={`nginx-tab ${nginxTab === 'maintenance' ? 'active' : ''}`} onClick={() => setNginxTab('maintenance')}>
            <AlertTriangle size={12} /> Maintenance
            {maintStatus === 'active' && <span className="maint-badge active">ON</span>}
          </button>
        </div>

        {/* ── VISUAL BUILDER TAB ───────────────────────────────────── */}
        {nginxTab === 'visual' && (
          <div className="vb-container">
            <div className="nginx-toolbar vb-file-toolbar">
              <span className="nginx-path">/etc/nginx/sites-enabled/</span>
              <select
                className="commit-select nginx-select"
                value={selectedFile}
                onChange={e => loadFile(e.target.value, { syncVisual: true })}
                disabled={loading}
              >
                {siteFiles.length === 0 && <option value="">No sites found</option>}
                {siteFiles.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
              {loading && <Loader2 size={14} className="spin" aria-label="Loading" />}
            </div>
            {error && (
              <div className="nginx-msg nginx-msg-err">
                <AlertTriangle size={12} />{error}
              </div>
            )}
            <div className="vb-scroll">
              <p className="vb-hint">Choose the same site file as in the <strong>Raw Editor</strong> (dropdown above). Changing the file loads it from the server and updates this form. Only domain, HTTPS, routes, and simple toggles appear here—use Raw for logs, headers, Certbot includes, etc., or use <strong>Import from Raw Editor</strong> after editing there.</p>

              <div className="vb-section">
                <div className="vb-section-title">Required</div>
                <div className="vb-grid-2">
                  <label className="vb-field vb-full">
                    <span>Server name (domain)</span>
                    <input className="cfg-input" placeholder="app.example.com" value={vcfg.serverName} onChange={e => pV({ serverName: e.target.value })} />
                  </label>
                </div>
                <div className="vb-sub">
                  <div className="vb-section-title">
                    <label className="vb-check vb-check-title"><input type="checkbox" checked={vcfg.ssl} onChange={e => pV({ ssl: e.target.checked })} /> HTTPS (SSL)</label>
                  </div>
                  {vcfg.ssl && (
                    <>
                      <p className="vb-ssl-hint">Certificate paths use the Certbot layout unless you override them under <strong>More options</strong>: <code>/etc/letsencrypt/live/&lt;domain&gt;/fullchain.pem</code> and <code>privkey.pem</code>.</p>
                      <div className="vb-ssl-toolbar">
                        <span className="vb-ssl-toolbar-title"><ShieldCheck size={14} aria-hidden /> Certificate on server</span>
                        {sslCertLoading ? (
                          <span className="vb-ssl-status vb-ssl-status-muted"><Loader2 className="spin" size={13} /> Checking…</span>
                        ) : sslCertStatus ? (
                          !sslCertStatus.exists ? (
                            <span className="vb-ssl-status vb-ssl-status-bad">No certificate at expected path</span>
                          ) : sslCertStatus.expired ? (
                            <span className="vb-ssl-status vb-ssl-status-bad">Expired or unreadable</span>
                          ) : (
                            <span className="vb-ssl-status vb-ssl-status-ok">
                              OK
                              {sslCertStatus.daysRemaining != null ? ` · ${sslCertStatus.daysRemaining}d left` : ''}
                              {sslCertStatus.notAfterDisplay ? ` · ${sslCertStatus.notAfterDisplay}` : ''}
                            </span>
                          )
                        ) : (
                          <span className="vb-ssl-status vb-ssl-status-muted">Not checked</span>
                        )}
                        <button type="button" className="btn btn-ghost btn-sm" disabled={sslCertLoading || !sslPrimaryHost} onClick={() => refreshSslStatus()}>
                          <RefreshCw size={12} /> Check
                        </button>
                        {sslNeedsCertObtain && (
                          <>
                            <input
                              type="email"
                              className="cfg-input vb-ssl-email"
                              placeholder="Email (Let's Encrypt)"
                              value={certbotEmail}
                              onChange={(e) => {
                                const v = e.target.value;
                                setCertbotEmail(v);
                                try { localStorage.setItem('vb-certbot-email', v); } catch { /* ignore */ }
                              }}
                              autoComplete="email"
                            />
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              disabled={sslCertbotRunning || sslCertLoading || !sslPrimaryHost || !certbotEmail.trim()}
                              onClick={() => runCertbotObtain()}
                            >
                              {sslCertbotRunning ? <Loader2 className="spin" size={12} /> : <Lock size={12} />}
                              {sslCertStatus?.exists && sslCertStatus?.expired ? 'Renew certificate' : 'Obtain certificate'}
                            </button>
                          </>
                        )}
                      </div>
                      <p className="vb-ssl-note">Requires Certbot on the server, DNS pointing here, and HTTP (port 80) reachable for validation. Uses <code>certbot certonly --nginx</code> for all hostnames in <strong>Server name</strong>.</p>
                      {sslCertMsg && <pre className="vb-ssl-msg">{sslCertMsg}</pre>}
                    </>
                  )}
                </div>
              </div>

              <div className="vb-section">
                <div className="vb-section-title">
                  Locations (routes)
                  <button className="btn btn-ghost btn-sm" onClick={() => { setLocForm({ ...BLANK_LOC(), id: Math.random().toString(36).slice(2) }); setLocEditId(null); setLocEditOpen(true); }}>
                    <Plus size={12} /> Add
                  </button>
                </div>
                {locEditOpen && locEditId === null && (
                  <div ref={locEditorRef} className="vb-loc-inline-wrap vb-loc-add-wrap">
                    <VbLocEditorForm
                      locForm={locForm}
                      pL={pL}
                      isNew
                      onSave={saveLocForm}
                      onCancel={() => { setLocEditOpen(false); setLocEditId(null); }}
                    />
                  </div>
                )}
                {vcfg.locations.length > 0 && (
                  <div className="vb-loc-list">
                    {vcfg.locations.map((loc) => (
                      <React.Fragment key={loc.id}>
                        <div
                          className={`vb-loc-row ${locEditId === loc.id ? 'vb-loc-editing' : ''} ${draggingLocId === loc.id ? 'vb-loc-dragging' : ''}`}
                          onDragOver={handleLocDragOver}
                          onDrop={(e) => handleLocDrop(e, loc.id)}
                        >
                          <span
                            className="vb-loc-drag"
                            draggable
                            title="Drag to reorder"
                            onDragStart={(e) => handleLocDragStart(e, loc.id)}
                            onDragEnd={handleLocDragEnd}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <GripVertical size={14} aria-hidden />
                          </span>
                          <code className="vb-loc-path">{loc.path}</code>
                          <span className={`vb-type-badge vb-type-${loc.type}`}>{LOC_TYPE_LABELS[loc.type]}</span>
                          <span className="vb-loc-detail">
                            {(loc.type === 'proxy' || loc.type === 'websocket') && (loc.upstream || '—')}
                            {loc.type === 'static' && (loc.root || '—')}
                            {loc.type === 'alias'  && (loc.aliasPath || '—')}
                            {loc.type === 'custom' && '(raw)'}
                          </span>
                          <div className="vb-loc-acts">
                            <button className="btn btn-ghost btn-xs" title="Edit" type="button" onClick={() => { setLocForm({ ...loc }); setLocEditId(loc.id); setLocEditOpen(true); }}><Pencil size={11} /></button>
                            <button className="btn btn-ghost btn-xs vb-del" title="Delete" type="button" onClick={() => pV({ locations: vcfg.locations.filter(l => l.id !== loc.id) })}><Trash2 size={11} /></button>
                          </div>
                        </div>
                        {locEditOpen && locEditId === loc.id && (
                          <div ref={locEditorRef} className="vb-loc-inline-wrap">
                            <VbLocEditorForm
                              locForm={locForm}
                              pL={pL}
                              isNew={false}
                              onSave={saveLocForm}
                              onCancel={() => { setLocEditOpen(false); setLocEditId(null); }}
                            />
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>

              <div className="vb-section vb-more">
                <button type="button" className="vb-more-toggle" onClick={() => setVbMoreOpen(o => !o)}>
                  {vbMoreOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  More options (listen IPv6, HTTP redirect, WebSockets)
                </button>
                {vbMoreOpen && (
                  <>
                    <div className="vb-grid-2 vb-more-inner">
                      <div className="vb-checks vb-full">
                        <label className="vb-check"><input type="checkbox" checked={vcfg.ipv6} onChange={e => pV({ ipv6: e.target.checked })} /> Listen on IPv6 [::]</label>
                        <label className="vb-check"><input type="checkbox" checked={vcfg.httpRedirect} disabled={!vcfg.ssl} onChange={e => pV({ httpRedirect: e.target.checked })} /> Extra HTTP→HTTPS server block (port 80)</label>
                        <label className="vb-check"><input type="checkbox" checked={vcfg.mapBlock} onChange={e => pV({ mapBlock: e.target.checked })} /> WebSocket map ($connection_upgrade)</label>
                      </div>
                    </div>
                  </>
                )}
              </div>

            </div>

            <div className="vb-bottom">
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setVcfg(content.trim() ? parseNginxVisualCfg(content) : BLANK_VCFG())}>
                <RefreshCw size={12} /> Import from Raw Editor
              </button>
              <div className="vb-bottom-right">
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setVcfg(BLANK_VCFG()); setLocEditOpen(false); setLocEditId(null); }}>
                  Clear form
                </button>
                <button type="button" className="btn btn-deploy btn-sm" onClick={() => { setContent(generateNginxConfig(vcfg)); setNginxTab('config'); setTestResult(null); }}>
                  <CheckCircle size={12} /> Apply to Raw Editor
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── CONFIG EDITOR TAB ─────────────────────────────────────── */}
        {nginxTab === 'config' && (
          <>
            {/* Toolbar */}
            <div className="nginx-toolbar">
              <span className="nginx-path">/etc/nginx/sites-enabled/</span>
              <select
                className="commit-select nginx-select"
                value={selectedFile}
                onChange={e => loadFile(e.target.value)}
                disabled={loading}
              >
                {siteFiles.length === 0 && <option value="">No sites found</option>}
                {siteFiles.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
              <button
                className={`btn btn-ghost btn-sm ${renameOpen ? 'active' : ''}`}
                onClick={() => { setRenameOpen(p => !p); setRenameTo(selectedFile); setRenameErr(''); setNewFileOpen(false); }}
                disabled={!selectedFile}
                title="Rename this site file"
              >
                <Pencil size={12} /> Rename
              </button>
              <button
                className={`btn btn-ghost btn-sm ${newFileOpen ? 'active' : ''}`}
                onClick={() => { setNewFileOpen(p => !p); setError(''); setRenameOpen(false); }}
                title="Create a new nginx site file"
              >
                <Plus size={12} /> New File
              </button>
              <div className="nginx-toolbar-btns">
                <button className="btn btn-ghost btn-sm" onClick={() => setQuickOpen(p => !p)} title="Quick Config generator">
                  {quickOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />} Quick Config
                </button>
                <button className="btn btn-ghost btn-sm" onClick={testConfig} disabled={testing || loading}>
                  {testing ? <Loader2 size={12} className="spin" /> : <CheckCircle size={12} />}
                  Test
                </button>
                <button className="btn btn-deploy btn-sm" onClick={saveConfig} disabled={saving || loading || !selectedFile}>
                  {saving ? <Loader2 size={12} className="spin" /> : <RefreshCw size={12} />}
                  Save & Reload
                </button>
              </div>
            </div>

            {/* Rename panel */}
            {renameOpen && (
              <div className="nginx-new-file">
                <div className="nginx-new-file-row">
                  <span className="nginx-path" style={{ flexShrink: 0 }}>{selectedFile} →</span>
                  <input
                    className="cfg-input nginx-new-name"
                    placeholder="new-filename"
                    value={renameTo}
                    onChange={e => { setRenameTo(e.target.value); setRenameErr(''); }}
                    onKeyDown={e => { if (e.key === 'Enter') renameFile(); if (e.key === 'Escape') setRenameOpen(false); }}
                    autoFocus
                  />
                  <button className="btn btn-deploy btn-sm" onClick={renameFile} disabled={renaming || !renameTo.trim() || renameTo.trim() === selectedFile}>
                    {renaming ? <Loader2 size={12} className="spin" /> : <CheckCircle size={12} />}
                    Rename
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => { setRenameOpen(false); setRenameErr(''); }}>
                    <XCircle size={12} />
                  </button>
                </div>
                {renameErr && <span className="nginx-new-hint" style={{ color: 'var(--red)' }}>{renameErr}</span>}
                <span className="nginx-new-hint">Renames the file in <code>/etc/nginx/sites-enabled/</code> and reloads nginx.</span>
              </div>
            )}

            {/* New File panel */}
            {newFileOpen && (
              <div className="nginx-new-file">
                <div className="nginx-new-file-row">
                  <input
                    className="cfg-input nginx-new-name"
                    placeholder="site-filename (e.g. app.example.com)"
                    value={newFileName}
                    onChange={e => setNewFileName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') createFile(); }}
                  />
                  <select className="commit-select" value={newTemplate} onChange={e => setNewTemplate(e.target.value)}>
                    {Object.entries(TEMPLATE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                  <button className="btn btn-deploy btn-sm" onClick={createFile} disabled={creating || !newFileName.trim()}>
                    {creating ? <Loader2 size={12} className="spin" /> : <Plus size={12} />}
                    Create
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => { setNewFileOpen(false); setNewFileName(''); }}>
                    <XCircle size={12} />
                  </button>
                </div>
                <span className="nginx-new-hint">File will be created in <code>/etc/nginx/sites-enabled/</code> and nginx will reload.</span>
              </div>
            )}

            {/* Quick Config panel */}
            {quickOpen && (
              <div className="nginx-quick">
                <div className="nginx-quick-grid">
                  <label className="nginx-quick-field">
                    <span>Server Name</span>
                    <input className="cfg-input" placeholder="app.example.com" value={quick.serverName} onChange={e => setQuick(p => ({ ...p, serverName: e.target.value }))} />
                  </label>
                  <label className="nginx-quick-field">
                    <span>Listen Port</span>
                    <input className="cfg-input" placeholder="80" value={quick.listenPort} onChange={e => setQuick(p => ({ ...p, listenPort: e.target.value }))} />
                  </label>
                  <label className="nginx-quick-field">
                    <span>Proxy Pass Port</span>
                    <input className="cfg-input" placeholder="3000" value={quick.proxyPort} onChange={e => setQuick(p => ({ ...p, proxyPort: e.target.value }))} />
                  </label>
                  <label className="nginx-quick-field nginx-quick-check">
                    <input type="checkbox" checked={quick.ssl} onChange={e => setQuick(p => ({ ...p, ssl: e.target.checked }))} />
                    <span>SSL</span>
                  </label>
                </div>
                <div className="nginx-quick-actions">
                  <select className="commit-select" value={newTemplate} onChange={e => setNewTemplate(e.target.value)}>
                    {Object.entries(TEMPLATE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                  <button className="btn btn-deploy btn-sm" onClick={applyQuickConfig}>
                    <CheckCircle size={12} /> Apply to Editor
                  </button>
                </div>
              </div>
            )}

            {/* Error */}
            {error && <div className="nginx-msg nginx-msg-err"><AlertTriangle size={12} />{error}</div>}

            {/* Test result */}
            {testResult && (
              <div className={`nginx-msg ${testResult.ok ? 'nginx-msg-ok' : 'nginx-msg-err'}`}>
                {testResult.ok ? <CheckCircle size={12} /> : <XCircle size={12} />}
                <pre>{testResult.output || (testResult.ok ? 'nginx -t: OK' : 'nginx -t: FAILED')}</pre>
              </div>
            )}

            {/* Editor */}
            <div className="nginx-editor">
              {loading
                ? <div className="nginx-loading"><Loader2 size={22} className="spin" /></div>
                : <textarea
                    className="nginx-textarea"
                    value={content}
                    onChange={e => { setContent(e.target.value); setTestResult(null); }}
                    spellCheck={false}
                    placeholder={selectedFile ? '' : 'Select a site file above or create a new one…'}
                  />
              }
            </div>
          </>
        )}

        {/* ── MAINTENANCE TAB ───────────────────────────────────────── */}
        {nginxTab === 'maintenance' && (
          <div className="nginx-maint">

            {/* Path + status row */}
            <div className="nginx-maint-top">
              <div className="nginx-maint-row">
                <label className="nginx-maint-label">Maintenance HTML Path</label>
                <div className="nginx-maint-field-row">
                  <input
                    className="cfg-input"
                    placeholder="/var/www/html/maintenance.html"
                    value={maintHtmlPath}
                    onChange={e => { setMaintHtmlPath(e.target.value); setPathSaved(false); }}
                    onBlur={e => {
                      const v = e.target.value.trim();
                      if (v) { savePathToDB(v); if (!maintHtml) loadMaintHtml(); }
                    }}
                  />
                  {pathSaving && <Loader2 size={12} className="spin" style={{ color: 'var(--t3)', flexShrink: 0 }} />}
                  {pathSaved  && <span className="nginx-path-saved"><CheckCircle size={11} /> Saved</span>}
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={loadMaintHtml}
                    disabled={!maintHtmlPath.trim() || maintHtmlLoading}
                    title="Reload HTML from server (overwrites any unsaved edits)"
                  >
                    {maintHtmlLoading ? <Loader2 size={12} className="spin" /> : <RefreshCw size={12} />}
                    Reload
                  </button>
                </div>
              </div>

              <div className="nginx-maint-row nginx-maint-status-row">
                <label className="nginx-maint-label">
                  Site file to toggle maintenance
                  {maintLoading && <Loader2 size={11} className="spin" style={{ marginLeft: 6 }} />}
                </label>
                <div className="nginx-maint-field-row">
                  <select
                    className="commit-select nginx-select"
                    value={maintFileName}
                    onChange={e => { setMaintFileName(e.target.value); setMaintStatus('unknown'); loadMaintStatus(e.target.value); }}
                    disabled={siteFiles.length === 0}
                  >
                    {siteFiles.length === 0 && <option value="">No sites found</option>}
                    {siteFiles.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                  {maintStatus !== 'unknown' && (
                    <span className={`maint-badge ${maintStatus}`}>
                      {maintStatus === 'active' ? '● Maintenance ON' : '○ Live'}
                    </span>
                  )}
                  {maintStatus === 'unknown' && maintLoading && <span className="maint-badge">Checking…</span>}
                  <button
                    className={`btn btn-sm ${maintStatus === 'active' ? 'btn-deploy' : 'btn-danger'}`}
                    onClick={toggleMaintenance}
                    disabled={toggling || !maintFileName || maintStatus === 'unknown'}
                  >
                    {toggling ? <Loader2 size={12} className="spin" /> : <AlertTriangle size={12} />}
                    {maintStatus === 'active' ? 'Disable Maintenance' : 'Enable Maintenance'}
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => loadMaintStatus(maintFileName)} disabled={!maintFileName || maintLoading}>
                    <RefreshCw size={12} />
                  </button>
                </div>
              </div>

              {maintMsg && (
                <div className={`nginx-msg ${maintMsg.ok ? 'nginx-msg-ok' : 'nginx-msg-err'}`}>
                  {maintMsg.ok ? <CheckCircle size={12} /> : <AlertTriangle size={12} />}
                  <pre>{maintMsg.text}</pre>
                </div>
              )}
            </div>

            {/* Maintenance HTML editor */}
            <div className="nginx-maint-editor-header">
              <span>Maintenance Page HTML</span>
              <button className="btn btn-deploy btn-sm" onClick={saveMaintHtml} disabled={savingHtml || !maintHtmlPath.trim()}>
                {savingHtml ? <Loader2 size={12} className="spin" /> : <CheckCircle size={12} />}
                Save Maintenance HTML
              </button>
            </div>
            <div className="nginx-editor" style={{ flex: 1 }}>
              {maintHtmlLoading
                ? <div className="nginx-loading"><Loader2 size={22} className="spin" /></div>
                : <textarea
                    className="nginx-textarea"
                    value={maintHtml}
                    onChange={e => setMaintHtml(e.target.value)}
                    spellCheck={false}
                    placeholder={'<!DOCTYPE html>\n<html>\n<body>\n  <h1>We\'ll be back soon!</h1>\n</body>\n</html>'}
                  />
              }
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CONFIG MODAL — Servers & Apps CRUD
// ─────────────────────────────────────────────────────────────────────────────

interface ServerForm { id: string | null; label: string; host: string; port: string; username: string; }
interface AppForm    { id: string | null; name: string; url: string; githubUrl: string; sourceDir: string; deployDir: string; gitBranch: string; buildFolder: string; installCmd: string; buildCmd: string; nginxSite: string; maintenancePath: string; serverIds: string[]; }

const BLANK_SRV: Omit<ServerForm, 'id'> = { label: '', host: '', port: '22', username: 'root' };
const BLANK_APP: Omit<AppForm,    'id'> = { name: '', url: '', githubUrl: '', sourceDir: '', deployDir: '', gitBranch: '', buildFolder: 'dist', installCmd: 'npm install', buildCmd: 'npm run build', nginxSite: '', maintenancePath: '', serverIds: [] };

interface CredProps {
  passwords:       Record<string, string>;
  setPassword:     (id: string, v: string) => void;
  showPw:          Record<string, boolean>;
  setShowPw:       React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  testConnection:  (server: ServerInfo) => void;
  serverStatus:    Record<string, { status: ServerStatus; info: string }>;
}

function ConfigModal({ onClose, onRefresh, cred }: { onClose: () => void; onRefresh: () => void; cred: CredProps }) {
  const [tab,     setTab]     = useState<'credentials' | 'servers' | 'apps'>('credentials');
  const [servers, setServers] = useState<ServerInfo[]>([]);
  const [apps,    setApps]    = useState<AppInfo[]>([]);
  const [sForm,   setSForm]   = useState<ServerForm | null>(null);
  const [aForm,   setAForm]   = useState<AppForm    | null>(null);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const overlayRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    const [sv, ap] = await Promise.all([
      apiFetch(`${API}/api/servers`).then(r => r.json()),
      apiFetch(`${API}/api/apps`).then(r => r.json()),
    ]);
    setServers(sv);
    setApps(ap);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  // ── Server CRUD ────────────────────────────────────────────────────────────
  const saveServer = async () => {
    if (!sForm) return;
    setSaving(true); setError('');
    try {
      const body = { label: sForm.label, host: sForm.host, port: parseInt(sForm.port) || 22, username: sForm.username || 'root' };
      const url    = sForm.id ? `${API}/api/servers/${sForm.id}` : `${API}/api/servers`;
      const method = sForm.id ? 'PUT' : 'POST';
      const r = await apiFetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).then(r => r.json());
      if (r.error) { setError(r.error); return; }
      setSForm(null); await load(); onRefresh();
    } finally { setSaving(false); }
  };

  const deleteServer = async (id: string) => {
    if (!window.confirm('Delete this server? This cannot be undone.')) return;
    await apiFetch(`${API}/api/servers/${id}`, { method: 'DELETE' });
    await load(); onRefresh();
  };

  // ── App CRUD ───────────────────────────────────────────────────────────────
  const saveApp = async () => {
    if (!aForm) return;
    setSaving(true); setError('');
    try {
      // serverIds: empty array → null (means "all servers") sent to backend
      const body   = { name: aForm.name, url: aForm.url, githubUrl: aForm.githubUrl, sourceDir: aForm.sourceDir, deployDir: aForm.deployDir, gitBranch: aForm.gitBranch, buildFolder: aForm.buildFolder, installCmd: aForm.installCmd, buildCmd: aForm.buildCmd, nginxSite: aForm.nginxSite, maintenancePath: aForm.maintenancePath, serverIds: aForm.serverIds.length > 0 ? aForm.serverIds : null };
      const url    = aForm.id ? `${API}/api/apps/${aForm.id}` : `${API}/api/apps`;
      const method = aForm.id ? 'PUT' : 'POST';
      const r = await apiFetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).then(r => r.json());
      if (r.error) { setError(r.error); return; }
      setAForm(null); await load(); onRefresh();
    } finally { setSaving(false); }
  };

  const deleteApp = async (id: string) => {
    if (!window.confirm('Delete this app? This cannot be undone.')) return;
    await apiFetch(`${API}/api/apps/${id}`, { method: 'DELETE' });
    await load(); onRefresh();
  };

  const pS = (patch: Partial<Omit<ServerForm,'id'>>) => setSForm(p => p ? { ...p, ...patch } : p);
  const pA = (patch: Partial<Omit<AppForm,'id'>>)    => setAForm(p => p ? { ...p, ...patch } : p);

  return (
    <div className="cfg-overlay" ref={overlayRef} onMouseDown={e => { if (e.target === overlayRef.current) onClose(); }}>
      <div className="cfg-dialog">

        {/* Header */}
        <div className="cfg-header">
          <span className="cfg-title"><Settings size={15} /> Configuration</span>
          <button className="cm-close" onClick={onClose}><XCircle size={16} /></button>
        </div>

        {/* Tabs */}
        <div className="cfg-tabs">
          <button className={`cfg-tab ${tab === 'credentials' ? 'active' : ''}`} onClick={() => { setTab('credentials'); setError(''); }}>
            <Lock size={13} /> Credentials
            {Object.values(cred.serverStatus).some(s => s.status === 'online') && (
              <span className="cfg-tab-badge ok"><CheckCircle size={10} /></span>
            )}
          </button>
          <button className={`cfg-tab ${tab === 'servers' ? 'active' : ''}`} onClick={() => { setTab('servers'); setError(''); setSForm(null); }}>
            <Server size={13} /> Servers
          </button>
          <button className={`cfg-tab ${tab === 'apps' ? 'active' : ''}`} onClick={() => { setTab('apps'); setError(''); setAForm(null); }}>
            <Globe size={13} /> Apps
          </button>
        </div>

        {error && <div className="cfg-error"><AlertTriangle size={12} />{error}</div>}

        <div className="cfg-body">

          {/* ══ CREDENTIALS TAB ══ */}
          {tab === 'credentials' && (
            <div className="cfg-section">
              <p className="cfg-cred-note"><Lock size={11} /> Passwords live only in your browser session — never saved to disk.</p>
              <div className="cfg-list">
                {servers.map(sv => {
                  const ss  = cred.serverStatus[sv.id] || { status: 'unknown' as ServerStatus, info: '' };
                  const pw  = cred.passwords[sv.id] || '';
                  const vis = cred.showPw[sv.id];
                  return (
                    <div key={sv.id} className="cfg-cred-row">
                      <div className="cfg-cred-server">
                        <div className="cred-dot" style={{ background: STATUS_COLOR[ss.status] }} />
                        <span className="cfg-item-flag" style={{ fontSize: '1rem' }}>{serverFlag(sv.label)}</span>
                        <div className="cred-info">
                          <strong>{sv.label}</strong>
                          <span>{sv.host}:{sv.port}</span>
                        </div>
                      </div>
                      <div className="cred-field cfg-cred-field">
                        <input
                          type={vis ? 'text' : 'password'}
                          placeholder={`Password for ${sv.label}`}
                          value={pw}
                          onChange={e => cred.setPassword(sv.id, e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && cred.testConnection(sv)}
                          className={`cfg-input ${pw ? 'filled' : ''}`}
                          autoComplete="off"
                        />
                        <button className="eye-btn" onClick={() => cred.setShowPw(p => ({ ...p, [sv.id]: !p[sv.id] }))} tabIndex={-1}>
                          {vis ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                      </div>
                      <div className="cfg-cred-actions">
                        <button className="btn btn-ghost btn-sm" onClick={() => cred.testConnection(sv)} disabled={ss.status === 'testing' || !pw.trim()}>
                          {ss.status === 'testing'  ? <><Loader2 size={12} className="spin" /> Testing…</>
                           : ss.status === 'online'  ? <><Wifi size={12} /> Connected</>
                           : ss.status === 'offline' ? <><WifiOff size={12} /> Retry</>
                           : <><RefreshCw size={12} /> Test SSH</>}
                        </button>
                        {ss.info && (
                          <div className={`cred-badge ${ss.status}`}>
                            {ss.status === 'online' ? <CheckCircle size={11} /> : <XCircle size={11} />}
                            <span>{ss.info}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                {servers.length === 0 && <p className="cfg-empty">No servers yet — add one in the Servers tab.</p>}
              </div>
              {servers.length > 0 && (
                <div className="cfg-cred-footer">
                  <button className="btn btn-ghost btn-sm" onClick={() => servers.forEach(s => cred.testConnection(s))} disabled={servers.some(s => !(cred.passwords[s.id] || '').trim())}>
                    <RefreshCw size={12} /> Test All Connections
                  </button>
                  {servers.every(s => (cred.passwords[s.id] || '').trim()) && (
                    <span className="badge-ok"><ShieldCheck size={11} /> All passwords entered</span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ══ SERVERS TAB ══ */}
          {tab === 'servers' && (
            <div className="cfg-section">
              <div className="cfg-list">
                {servers.map(sv => (
                  <div key={sv.id} className="cfg-item">
                    <div className="cfg-item-flag">{serverFlag(sv.label)}</div>
                    <div className="cfg-item-info">
                      <strong>{sv.label}</strong>
                      <span>{sv.username}@{sv.host}:{sv.port}</span>
                    </div>
                    <div className="cfg-item-actions">
                      <button className="btn btn-ghost btn-sm" onClick={() => { setSForm({ id: sv.id, label: sv.label, host: sv.host, port: String(sv.port), username: sv.username }); setError(''); }}>
                        <Pencil size={12} /> Edit
                      </button>
                      <button className="btn btn-sm cfg-del-btn" title="Delete" onClick={() => deleteServer(sv.id)}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                ))}
                {servers.length === 0 && <p className="cfg-empty">No servers configured yet.</p>}
              </div>

              {sForm ? (
                <div className="cfg-form">
                  <div className="cfg-form-title">{sForm.id ? 'Edit Server' : 'Add Server'}</div>
                  <div className="cfg-fields">
                    <label className="cfg-field">
                      <span>Label <em className="cfg-req">*</em></span>
                      <input className="cfg-input" placeholder="India Server" value={sForm.label} onChange={e => pS({ label: e.target.value })} />
                    </label>
                    <label className="cfg-field">
                      <span>Host <em className="cfg-req">*</em></span>
                      <input className="cfg-input" placeholder="cad.mapnexa.com" value={sForm.host} onChange={e => pS({ host: e.target.value })} />
                    </label>
                    <label className="cfg-field cfg-field-sm">
                      <span>Port</span>
                      <input className="cfg-input" type="number" min={1} max={65535} value={sForm.port} onChange={e => pS({ port: e.target.value })} />
                    </label>
                    <label className="cfg-field cfg-field-sm">
                      <span>Username</span>
                      <input className="cfg-input" placeholder="root" value={sForm.username} onChange={e => pS({ username: e.target.value })} />
                    </label>
                  </div>
                  <div className="cfg-form-actions">
                    <button className="btn btn-ghost btn-sm" onClick={() => { setSForm(null); setError(''); }}>Cancel</button>
                    <button className="btn btn-deploy btn-sm" onClick={saveServer} disabled={saving || !sForm.label.trim() || !sForm.host.trim()}>
                      {saving ? <Loader2 size={12} className="spin" /> : <CheckCircle size={12} />}
                      {sForm.id ? 'Save Changes' : 'Add Server'}
                    </button>
                  </div>
                </div>
              ) : (
                <button className="btn btn-ghost btn-sm cfg-add-btn" onClick={() => { setSForm({ id: null, ...BLANK_SRV }); setError(''); }}>
                  <Plus size={13} /> Add Server
                </button>
              )}
            </div>
          )}

          {/* ══ APPS TAB ══ */}
          {tab === 'apps' && (
            <div className="cfg-section">
              <div className="cfg-list">
                {apps.map(ap => (
                  <div key={ap.id} className="cfg-item">
                    <div className="cfg-item-flag"><Globe size={16} style={{ color: 'var(--blue)' }} /></div>
                    <div className="cfg-item-info">
                      <strong>{ap.name}</strong>
                      <span>{ap.url || ap.sourceDir}</span>
                    </div>
                    <div className="cfg-item-actions">
                      <button className="btn btn-ghost btn-sm" onClick={() => { setAForm({ id: ap.id, name: ap.name, url: ap.url || '', githubUrl: ap.githubUrl || '', sourceDir: ap.sourceDir, deployDir: ap.deployDir, gitBranch: ap.gitBranch || '', buildFolder: ap.buildFolder || 'dist', installCmd: ap.installCmd || 'npm install', buildCmd: ap.buildCmd || 'npm run build', nginxSite: ap.nginxSite || '', maintenancePath: ap.maintenancePath || '', serverIds: ap.serverIds || servers.map(s => s.id) }); setError(''); }}>
                        <Pencil size={12} /> Edit
                      </button>
                      <button className="btn btn-sm cfg-del-btn" title="Delete" onClick={() => deleteApp(ap.id)}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                ))}
                {apps.length === 0 && <p className="cfg-empty">No apps configured yet.</p>}
              </div>

              {aForm ? (
                <div className="cfg-form">
                  <div className="cfg-form-title">{aForm.id ? 'Edit App' : 'Add App'}</div>
                  <div className="cfg-fields cfg-fields-2col">
                    <label className="cfg-field">
                      <span>App Name <em className="cfg-req">*</em></span>
                      <input className="cfg-input" placeholder="App MapNexa" value={aForm.name} onChange={e => pA({ name: e.target.value })} />
                    </label>
                    <label className="cfg-field">
                      <span>Live URL</span>
                      <input className="cfg-input" placeholder="app.mapnexa.com" value={aForm.url} onChange={e => pA({ url: e.target.value })} />
                    </label>
                    <div className="cfg-field-full cfg-repo-row">
                      <label className="cfg-field cfg-repo-url">
                        <span><Github size={11} style={{ display: 'inline', marginRight: 4 }} />GitHub URL</span>
                        <input className="cfg-input" placeholder="https://github.com/org/repo" value={aForm.githubUrl} onChange={e => pA({ githubUrl: e.target.value })} />
                      </label>
                      <label className="cfg-field cfg-repo-branch">
                        <span>Git Branch</span>
                        <input className="cfg-input" placeholder="main" value={aForm.gitBranch} onChange={e => pA({ gitBranch: e.target.value })} />
                      </label>
                    </div>
                    <label className="cfg-field cfg-field-full">
                      <span>Source Dir (on server) <em className="cfg-req">*</em></span>
                      <input className="cfg-input" placeholder="/root/app.mapnexa.com/frontend" value={aForm.sourceDir} onChange={e => pA({ sourceDir: e.target.value })} />
                    </label>
                    <label className="cfg-field cfg-field-full">
                      <span>Deploy Dir (nginx web root) <em className="cfg-req">*</em></span>
                      <input className="cfg-input" placeholder="/var/www/appmapnexa" value={aForm.deployDir} onChange={e => pA({ deployDir: e.target.value })} />
                    </label>
                    <label className="cfg-field">
                      <span><FileCode2 size={11} style={{ display: 'inline', marginRight: 4 }} />Nginx Site Name</span>
                      <input className="cfg-input" placeholder="app.mapnexa.com" value={aForm.nginxSite} onChange={e => pA({ nginxSite: e.target.value })} />
                    </label>
                    <label className="cfg-field">
                      <span><AlertTriangle size={11} style={{ display: 'inline', marginRight: 4 }} />Maintenance HTML Path</span>
                      <input className="cfg-input" placeholder="/var/www/html/maintenance.html" value={aForm.maintenancePath} onChange={e => pA({ maintenancePath: e.target.value })} />
                    </label>

                    {/* Server selection */}
                    <div className="cfg-field cfg-field-full">
                      <span>Deploy to Servers <em className="cfg-req">*</em></span>
                      <div className="cfg-srv-checks">
                        {servers.map(sv => {
                          const checked = aForm.serverIds.includes(sv.id);
                          return (
                            <label key={sv.id} className={`cfg-srv-chip ${checked ? 'selected' : ''}`}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => {
                                  const next = checked
                                    ? aForm.serverIds.filter(id => id !== sv.id)
                                    : [...aForm.serverIds, sv.id];
                                  pA({ serverIds: next });
                                }}
                              />
                              <span>{serverFlag(sv.label)}</span>
                              <span>{sv.label}</span>
                            </label>
                          );
                        })}
                        {servers.length === 0 && <span className="cfg-empty" style={{ padding: '6px 0' }}>No servers configured yet — add one in the Servers tab.</span>}
                      </div>
                    </div>

                    <label className="cfg-field">
                      <span>Build Folder</span>
                      <input className="cfg-input" placeholder="dist" value={aForm.buildFolder} onChange={e => pA({ buildFolder: e.target.value })} />
                    </label>
                    <label className="cfg-field">
                      <span>Install Command</span>
                      <input className="cfg-input" placeholder="npm install" value={aForm.installCmd} onChange={e => pA({ installCmd: e.target.value })} />
                    </label>
                    <label className="cfg-field">
                      <span>Build Command</span>
                      <input className="cfg-input" placeholder="npm run build" value={aForm.buildCmd} onChange={e => pA({ buildCmd: e.target.value })} />
                    </label>
                  </div>
                  <div className="cfg-form-actions">
                    <button className="btn btn-ghost btn-sm" onClick={() => { setAForm(null); setError(''); }}>Cancel</button>
                    <button className="btn btn-deploy btn-sm" onClick={saveApp} disabled={saving || !aForm.name.trim() || !aForm.sourceDir.trim() || !aForm.deployDir.trim()}>
                      {saving ? <Loader2 size={12} className="spin" /> : <CheckCircle size={12} />}
                      {aForm.id ? 'Save Changes' : 'Add App'}
                    </button>
                  </div>
                </div>
              ) : (
                <button className="btn btn-ghost btn-sm cfg-add-btn" onClick={() => { setAForm({ id: null, ...BLANK_APP, serverIds: servers.map(s => s.id) }); setError(''); }}>
                  <Plus size={13} /> Add App
                </button>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ACTIVE-DEPLOYMENT PERSISTENCE  (survives page refresh)
// ─────────────────────────────────────────────────────────────────────────────
const ACTIVE_KEY = 'active_deployments';

function loadActiveDeployments(): Record<string, string> {
  try { return JSON.parse(sessionStorage.getItem(ACTIVE_KEY) || '{}'); } catch { return {}; }
}
function saveActiveDeployment(stateKey: string, deployId: string) {
  try {
    const cur = loadActiveDeployments();
    cur[stateKey] = deployId;
    sessionStorage.setItem(ACTIVE_KEY, JSON.stringify(cur));
  } catch {}
}
function removeActiveDeployment(stateKey: string) {
  try {
    const cur = loadActiveDeployments();
    delete cur[stateKey];
    sessionStorage.setItem(ACTIVE_KEY, JSON.stringify(cur));
  } catch {}
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN APP
// ─────────────────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <HashRouter>
      <AppShell />
    </HashRouter>
  );
}

function AppShell() {
  const navigate = useNavigate();
  const authed = !!getToken();

  // ── Theme ──────────────────────────────────────────────────────────────────
  const [theme, setThemeState] = useState<Theme>(() =>
    (localStorage.getItem('theme') as Theme) || 'system'
  );
  const setTheme = (t: Theme) => {
    setThemeState(t);
    document.documentElement.setAttribute('data-theme', t);
    localStorage.setItem('theme', t);
  };
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, []); // eslint-disable-line

  const [servers, setServers]             = useState<ServerInfo[]>([]);
  const [apps,    setApps]                = useState<AppInfo[]>([]);
  const [serverStatus, setServerStatus]   = useState<Record<string, { status: ServerStatus; info: string }>>({});
  const [deployStates, setDeployStates]   = useState<Record<string, DeployState>>({});
  const [passwords,    setPasswords]      = useState<Record<string, string>>(loadPasswords);
  const [showPw,       setShowPw]         = useState<Record<string, boolean>>({});
  const [configOpen,   setConfigOpen]     = useState(false);
  const [nginxTarget,  setNginxTarget]    = useState<NginxTarget | null>(null);
  // backups
  const [backups,        setBackups]          = useState<Record<string, Backup[]>>({});
  const [backupsLoading, setBackupsLoading]   = useState<Record<string, boolean>>({});
  const [selectedBackup, setSelectedBackup]   = useState<Record<string, string>>({});
  // commits
  const [commits,        setCommits]          = useState<Record<string, CommitInfo[]>>({});
  const [commitsLoading, setCommitsLoading]   = useState<Record<string, boolean>>({});
  const [commitHash,     setCommitHash]       = useState<Record<string, string>>({});
  const [commitModalKey, setCommitModalKey]   = useState<string | null>(null);
  // op tracking
  const [lastOp, setLastOp] = useState<Record<string, 'deploy' | 'rollback'>>({});

  const loadConfig = useCallback(() => {
    apiFetch(`${API}/api/servers`).then((r) => r.json()).then((data: ServerInfo[]) => {
      setServers(data);
      setServerStatus((prev) => {
        const next = { ...prev };
        data.forEach((s) => { if (!next[s.id]) next[s.id] = { status: 'unknown', info: '' }; });
        return next;
      });
    });
    apiFetch(`${API}/api/apps`).then((r) => r.json()).then(setApps);
  }, []);

  useEffect(() => {
    if (!authed) return;
    loadConfig();
  }, [loadConfig, authed]);

  const patchDeploy = useCallback((key: string, patch: Partial<DeployState>) => {
    setDeployStates((prev) => ({
      ...prev,
      [key]: { ...{ logs: [], logsOpen: false, deployId: null, status: 'idle' as DeployStatus }, ...prev[key], ...patch },
    }));
  }, []);

  const getDS = (key: string): DeployState =>
    deployStates[key] || { status: 'idle', deployId: null, logs: [], logsOpen: false };

  const pollLogs = useCallback((deployId: string, stateKey: string) => {
    let last = 0;
    const timer = setInterval(async () => {
      try {
        const data = await apiFetch(`${API}/api/logs/${deployId}`).then((r) => r.json());
        const logs: LogEntry[] = data.logs || [];
        patchDeploy(stateKey, { logs });
        if (logs.length > last) {
          last = logs.length;
          const tail   = logs[logs.length - 1]?.message || '';
          const done   = /completed successfully|deployed successfully|All servers:|Everything deployed|Rollback of .* completed successfully/i.test(tail);
          const failed = /DEPLOYMENT FAILED|Fatal|failed|FAILED/i.test(tail) && !/not found/i.test(tail);
          if (done)   { clearInterval(timer); removeActiveDeployment(stateKey); patchDeploy(stateKey, { status: 'success' }); }
          if (failed) { clearInterval(timer); removeActiveDeployment(stateKey); patchDeploy(stateKey, { status: 'error' }); }
        }
      } catch (_) {}
    }, 600);
    // Safety net: stop polling after 10 minutes regardless
    setTimeout(() => { clearInterval(timer); removeActiveDeployment(stateKey); }, 10 * 60 * 1000);
  }, [patchDeploy]);

  // Resume any deployments that were running before the page was refreshed
  useEffect(() => {
    if (!authed) return;
    const active = loadActiveDeployments();
    Object.entries(active).forEach(([stateKey, deployId]) => {
      patchDeploy(stateKey, { status: 'running', deployId, logsOpen: true });
      pollLogs(deployId, stateKey);
    });
  }, [authed, patchDeploy, pollLogs]);

  const pwPayload = () => Object.fromEntries(Object.entries(passwords).filter(([, v]) => v.trim()));
  const missingPw = servers.filter((s) => !passwords[s.id]?.trim()).map((s) => s.label);

  const setPassword = (id: string, v: string) => {
    const next = { ...passwords, [id]: v };
    setPasswords(next); savePasswords(next);
  };

  const testConnection = async (server: ServerInfo) => {
    if (!passwords[server.id]?.trim()) {
      setServerStatus((p) => ({ ...p, [server.id]: { status: 'offline', info: 'Enter password first' } }));
      return;
    }
    setServerStatus((p) => ({ ...p, [server.id]: { status: 'testing', info: '' } }));
    try {
      const data = await apiFetch(`${API}/api/servers/${server.id}/test`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passwords: pwPayload() }),
      }).then((r) => r.json());
      setServerStatus((p) => ({ ...p, [server.id]: { status: data.success ? 'online' : 'offline', info: data.info || data.error } }));
    } catch {
      setServerStatus((p) => ({ ...p, [server.id]: { status: 'offline', info: 'Cannot reach backend' } }));
    }
  };

  const startDeploy = useCallback(async (url: string, key: string, pw: Record<string, string>, extra: Record<string, any> = {}) => {
    patchDeploy(key, { status: 'running', logs: [], logsOpen: true });
    try {
      const data = await apiFetch(`${API}${url}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passwords: pw, ...extra }),
      }).then((r) => r.json());
      if (data.deployId) { saveActiveDeployment(key, data.deployId); patchDeploy(key, { deployId: data.deployId }); pollLogs(data.deployId, key); }
      else patchDeploy(key, { status: 'error', logs: [{ time: new Date().toISOString(), message: data.error || 'Unknown error' }] });
    } catch (e: any) {
      patchDeploy(key, { status: 'error', logs: [{ time: new Date().toISOString(), message: e.message }] });
    }
  }, [patchDeploy, pollLogs]);

  const fetchBackups = useCallback(async (appId: string, serverId: string) => {
    const key = `${appId}__${serverId}`;
    setBackupsLoading((p) => ({ ...p, [key]: true }));
    try {
      const data = await apiFetch(`${API}/api/backups/${appId}/${serverId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passwords: pwPayload() }),
      }).then((r) => r.json());
      setBackups((p) => ({ ...p, [key]: data.backups || [] }));
    } catch {
      setBackups((p) => ({ ...p, [key]: [] }));
    } finally {
      setBackupsLoading((p) => ({ ...p, [key]: false }));
    }
  // eslint-disable-line
  }, [passwords]);

  const fetchCommits = useCallback(async (appId: string, serverId: string) => {
    const key = `${appId}__${serverId}`;
    setCommitsLoading((p) => ({ ...p, [key]: true }));
    try {
      const data = await apiFetch(`${API}/api/commits/${appId}/${serverId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passwords: pwPayload() }),
      }).then((r) => r.json());
      setCommits((p) => ({ ...p, [key]: data.commits || [] }));
    } catch {
      setCommits((p) => ({ ...p, [key]: [] }));
    } finally {
      setCommitsLoading((p) => ({ ...p, [key]: false }));
    }
  // eslint-disable-line
  }, [passwords]);

  const doRollback = useCallback(async (appId: string, serverId: string, stateKey: string) => {
    const backupFile = selectedBackup[stateKey] || null;
    const url  = serverId === 'all' ? `/api/rollback/${appId}/all` : `/api/rollback/${appId}/${serverId}`;
    const extra = backupFile ? { backupFile } : {};
    setLastOp((p) => ({ ...p, [stateKey]: 'rollback' }));
    await startDeploy(url, `rb__${stateKey}`, pwPayload(), extra);
  // eslint-disable-line
  }, [selectedBackup, passwords, startDeploy]);

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <>
      <Routes>
        <Route path="/login" element={authed ? <Navigate to="/" replace /> : <LoginPage />} />
        <Route element={authed ? <Outlet /> : <Navigate to="/login" replace />}>
          <Route
            element={(
              <AppLayout
                theme={theme}
                setTheme={setTheme}
                serversCount={servers.length}
                appsCount={apps.length}
                missingPwLabels={missingPw}
                serversConfigured={servers.length > 0}
                onOpenConfig={() => setConfigOpen(true)}
                onLogout={() => {
                  clearToken();
                  navigate('/login', { replace: true });
                }}
              />
            )}
          >
          <Route path="/" element={<DashboardPage servers={servers} apps={apps} serverStatus={serverStatus} />} />
          <Route
            path="/deploy"
            element={(
              <>
                <DeployPage
                  servers={servers}
                  apps={apps}
                  passwords={passwords}
                  serverStatus={serverStatus}
                  getDS={getDS}
                  patchDeploy={patchDeploy}
                  setLastOp={setLastOp}
                  startDeploy={startDeploy}
                  pwPayload={pwPayload}
                  fetchCommits={fetchCommits}
                  commitsLoading={commitsLoading}
                  commits={commits}
                  commitHash={commitHash}
                  setCommitHash={setCommitHash}
                  setCommitModalKey={setCommitModalKey}
                  fetchBackups={fetchBackups}
                  backupsLoading={backupsLoading}
                  backups={backups}
                  selectedBackup={selectedBackup}
                  setSelectedBackup={setSelectedBackup}
                  doRollback={doRollback}
                  lastOp={lastOp}
                  setNginxTarget={setNginxTarget}
                />
                {commitModalKey && commits[commitModalKey] && (
                  <CommitModal
                    commits={commits[commitModalKey]}
                    selected={commitHash[commitModalKey] || ''}
                    onSelect={(hash) => setCommitHash((p) => ({ ...p, [commitModalKey]: hash }))}
                    onClose={() => setCommitModalKey(null)}
                  />
                )}
                {nginxTarget && (
                  <NginxModal
                    target={nginxTarget}
                    passwords={passwords}
                    onClose={() => setNginxTarget(null)}
                  />
                )}
              </>
            )}
          />
          <Route
            path="/servers"
            element={(
              <ServerOpsPage
                servers={servers}
                passwords={passwords}
                setPassword={setPassword}
                showPw={showPw}
                setShowPw={setShowPw}
                serverStatus={serverStatus}
                testConnection={testConnection}
              />
            )}
          />
          <Route
            path="/dns"
            element={(
              <BindDnsPage
                servers={servers}
                passwords={passwords}
                setPassword={setPassword}
                showPw={showPw}
                setShowPw={setShowPw}
                serverStatus={serverStatus}
                testConnection={testConnection}
              />
            )}
          />
          <Route
            path="/database"
            element={(
              <DatabasePage
                servers={servers}
                passwords={passwords}
                setPassword={setPassword}
                showPw={showPw}
                setShowPw={setShowPw}
                serverStatus={serverStatus}
                testConnection={testConnection}
              />
            )}
          />
          <Route
            path="/services"
            element={(
              <SystemdPage
                servers={servers}
                passwords={passwords}
                setPassword={setPassword}
                showPw={showPw}
                setShowPw={setShowPw}
                serverStatus={serverStatus}
                testConnection={testConnection}
              />
            )}
          />
          </Route>
        </Route>
      </Routes>

      {authed && configOpen && (
        <ConfigModal
          onClose={() => setConfigOpen(false)}
          onRefresh={loadConfig}
          cred={{ passwords, setPassword, showPw, setShowPw, testConnection, serverStatus }}
        />
      )}
    </>
  );
}
